import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useQuery } from '@tanstack/react-query'
import { FormHandles } from '@unform/core'
import { Flex } from '@vtex/shoreline'

import DateInterval from '~/components/DateInterval'
import Line from '~/components/Line'
import {
  Checkbox,
  Label,
  Input,
  InputMoney,
  FormGroup
} from '~/components/unform'
import { getDaysBetweenDates, formatMoneyV2 } from '~/helpers'
import { useAppInfo } from '~/hooks/useAppInfo'
import useQueryParams from '~/hooks/useQuery'
import { getMinCostService } from '~/modules/retailMedia/services/campaign/min-cost'
import { useAppSelector } from '~/store/hooks'
import { useCampaignForm } from '~/modules/retailMedia/components/CampaignForm/hooks/useCampaignForm'

import { Alerts } from './elements/Alerts'
import { Header } from './elements/Header'
import { minCostDTO } from './minCostDTO'

import * as S from './styles'

const Setup = ({ initialData, formRef }: SetupProps<FormHandles>) => {
  const { t } = useTranslation()
  const { lengthCentsCurrency, isWhiteLabel } = useAppInfo()
  const { allowAgeRestrictedCampaign } = useCampaignForm()

  const [dailyBudget, setDailyBudget] = useState<number | null>(null)
  const [period, setPeriod] = useState<CallbackDateInterval>({
    interval: getDaysBetweenDates(initialData?.start_at, initialData?.end_at),
    startDate: initialData?.start_at,
    endDate: initialData?.end_at
  })

  /**
   * Params
   */
  const params = useQueryParams()

  const goal = useMemo(
    () => (params.get('goal') || initialData?.goal) as Goals,
    [params, initialData]
  )

  /**
   * Get campaign products
   */
  const { selectedProducts } = useAppSelector(
    state => state.retailMedia.campaign.products
  )

  /**
   * Handle min costs

  * Technical debt
   * The min cost service requires a minimum payload to work correctly:
   * - At least one product in `body.products`
   * - A defined `body.start_at` (campaign start date)
   * - A defined `body.publisher_id`, unless we are in white-label mode (`isWhiteLabel`)
   *
   * These constraints are validated by `minCostsPayloadIsValid` before calling
   * the service. If the service contract changes, keep this comment and
   * `minCostsPayloadIsValid` in sync with the new minimum requirements.
   */

  const minCostsParams: GetMinCostsRequest = useMemo(
    () => ({
      body: minCostDTO({
        selectedProducts,
        goal,
        publisherId: params.get('rmid') as PublisherId,
        period
      })
    }),
    [goal, params, period, selectedProducts]
  )

  const minCostsPayloadIsValid = useMemo(() => {
    const { body } = minCostsParams
    const hasProducts = body?.products?.length > 0
    const hasStartDate = body?.start_at
    const hasPublisherId = body?.publisher_id || isWhiteLabel

    return !!(hasProducts && hasStartDate && hasPublisherId)
  }, [isWhiteLabel, minCostsParams])

  const { data: minCostsData, error: minCostsError } = useQuery({
    queryKey: ['minCosts', minCostsParams],
    queryFn: async () => {
      const response = await getMinCostService(minCostsParams)

      return response.data
    },
    placeholderData: previousData => previousData,
    enabled: minCostsPayloadIsValid
  })

  const dailyBudgetValues = useMemo(
    () => ({
      min: minCostsData?.min_daily_budget,
      recommended: minCostsData?.suggested_daily_budget
    }),
    [minCostsData]
  )

  useEffect(() => {
    if (!minCostsData) return

    // Only apply the default daily budget if the user has not set a value yet.
    // We check both the local state (updated on user input) and the current form value.

    if (dailyBudget !== null && dailyBudget !== undefined) {
      return
    }

    const currentFormValue =
      formRef.current && 'getFieldValue' in formRef.current
        ? formRef.current.getFieldValue('daily_budget')?.value
        : undefined

    if (
      currentFormValue !== null &&
      currentFormValue !== undefined &&
      currentFormValue !== ''
    ) {
      return
    }

    // If the user has not set a value yet, we set the recommended daily budget
    const value = formatMoneyV2({
      value:
        dailyBudgetValues?.recommended || minCostsData?.min_daily_budget || 0
    })
    formRef?.current?.setFieldValue('daily_budget', value)
  }, [minCostsData, formRef, dailyBudgetValues, dailyBudget])

  /**
   * Handles
   */

  const handleChangeDailyBudget = useCallback(
    (e: InputMoneyValuesOnChange) => {
      const value = lengthCentsCurrency
        ? e?.floatValue / 100
        : e?.floatValue || 0

      setDailyBudget(isNaN(value) ? null : value)
    },
    [lengthCentsCurrency]
  )

  const initialDataDateInterval: InitialDataDateInterval = {
    start_at: initialData?.start_at,
    end_at: initialData?.end_at
  }

  return (
    <div>
      <Header goal={goal} />

      <Line margin="1rem" />

      <Flex direction="column" gap="var(--sl-space-2)">
        <S.FormWrapper>
          <S.FormGroupName>
            <Label htmlFor="name" isRequired>
              {t('rm:objectiveBased.setup.campaign.name')}
            </Label>
            <Input name="name" required />
          </S.FormGroupName>

          <S.BudgetAndDateWrapper>
            <FormGroup>
              <Label htmlFor="daily_budget" isRequired>
                {t('rm:objectiveBased.setup.campaign.dailyBudget')}
              </Label>
              <InputMoney
                required
                name="daily_budget"
                onChange={e => handleChangeDailyBudget(e)}
              />
            </FormGroup>

            <DateInterval
              initialData={initialDataDateInterval}
              onChange={setPeriod}
              showTooltip
            />
          </S.BudgetAndDateWrapper>
        </S.FormWrapper>

        <Alerts
          dailyBudget={dailyBudget}
          period={period}
          dailyBudgetValues={dailyBudgetValues}
          minCostsError={minCostsError}
        />

        {allowAgeRestrictedCampaign && (
          <FormGroup>
            <Checkbox
              name="settings.age_restricted"
              label={t('rm:campaign.settings.ageRestricted.label')}
            />
          </FormGroup>
        )}
      </Flex>
    </div>
  )
}

export default React.memo(Setup)
